import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { AIChatbotBaseLoggingService } from 'src/app/ai-chatbot/services/ai-chatbot-base-logging/ai-chatbot-base-logging.service';

/** Parameters for a single editor image generation event (editor_events row). */
export interface EditorImageEventParams {
  /** Editor session id reused for both generation and feedback logs. */
  sessionId: string;
  /** templateEditorService.presentation?.id; may be missing before first save. */
  presentationId?: string;
  /** The generated image id — used as event_id (links events ↔ feedback). */
  imageId: string;
  /** The prompt text sent to the backend (not echoed back in the response). */
  prompt?: string;
  model?: string;
  /** Packed into content.image_url. */
  imageUrl?: string;
  /** Packed into content.aspect_ratio. */
  aspectRatio?: string;
  /** Client-side timing: request dispatch → response receipt. */
  durationMs?: number;
  creditsUsed?: number;
}

/** Parameters for a single editor text improvement event (editor_events row). */
export interface EditorTextEventParams {
  /** Editor session id generated on each panel open. */
  sessionId: string;
  /** templateEditorService.presentation?.id; may be missing before first save. */
  presentationId?: string;
  /** Client-generated id for this improvement — used as event_id. */
  eventId: string;
  /** The user-typed custom prompt; preset/tone actions send none. */
  prompt?: string;
  /** Packed into content.action_id. */
  actionId: string;
  /** Packed into content.action_category. */
  actionCategory: string;
  /** The slide text sent for improvement — packed into content.text_input. */
  textInput?: string;
  /** The AI-generated text — packed into content.text_output. */
  textOutput?: string;
  /** Client-side timing: request dispatch → response receipt. */
  durationMs?: number;
  creditsUsed?: number;
}

/** Parameters for a single editor image feedback event (editor_feedback row). */
export interface EditorImageFeedbackParams {
  sessionId?: string;
  /** The image being rated — used as event_id. */
  imageId: string;
  rating: 'good' | 'bad';
  message?: string;
}

/**
 * Logs Editor AI Tools events to BigQuery (editor_events / editor_feedback).
 * Reuses the shared base logger for OAuth token handling, insertAll, and the
 * common fields. Covers image generation and text improvement events under
 * the same session/event model.
 */
@Injectable({ providedIn: 'root' })
export class EditorToolsLoggingService extends AIChatbotBaseLoggingService {
  /** Guards against double-logging the same image on re-render / retry. */
  private loggedEventIds = new Set<string>();

  constructor(
    http: HttpClient,
    userStateService: UserStateService,
    companyStateService: CompanyStateService,
  ) {
    super(http, userStateService, companyStateService);
  }

  /** Writes one row to editor_events. Call once per successful generation. */
  logImageGenerated(params: EditorImageEventParams): Observable<any> {
    if (this.loggedEventIds.has(params.imageId)) {
      return of(null);
    }
    this.loggedEventIds.add(params.imageId);

    const jsonData = {
      ...this.buildCommonFields(),
      session_id: params.sessionId ?? null,
      presentation_id: params.presentationId ?? null,
      event_id: params.imageId,
      event_type: 'create_image',
      prompt: params.prompt ?? null,
      model: params.model ?? null,
      duration_ms: params.durationMs ?? null,
      credits_used: params.creditsUsed ?? null,
      content: JSON.stringify({
        image_url: params.imageUrl ?? null,
        aspect_ratio: params.aspectRatio ?? null,
      }),
    };

    return this.insertData(this.getTableUrl('editor_events'), jsonData);
  }

  /** Writes one row to editor_events. Call once per successful improvement. */
  logTextImproved(params: EditorTextEventParams): Observable<any> {
    const jsonData = {
      ...this.buildCommonFields(),
      session_id: params.sessionId ?? null,
      presentation_id: params.presentationId ?? null,
      event_id: params.eventId,
      event_type: 'improve_text',
      prompt: params.prompt ?? null,
      // improve-text responses do not include the model yet.
      model: null,
      duration_ms: params.durationMs ?? null,
      credits_used: params.creditsUsed ?? null,
      content: JSON.stringify({
        action_id: params.actionId,
        action_category: params.actionCategory,
        text_input: params.textInput ?? null,
        text_output: params.textOutput ?? null,
      }),
    };

    return this.insertData(this.getTableUrl('editor_events'), jsonData);
  }

  /** Writes one row to editor_feedback. Call on each 👍/👎 submit. */
  logImageFeedback(params: EditorImageFeedbackParams): Observable<any> {
    const jsonData = {
      ...this.buildCommonFields(),
      session_id: params.sessionId ?? null,
      event_id: params.imageId,
      feedback_rating: params.rating,
      feedback_message: params.message ?? null,
    };

    return this.insertData(this.getTableUrl('editor_feedback'), jsonData);
  }
}
